import React, { useEffect, useRef, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import Modal from 'components/atoms/Modal'
import { selectBpFilesList } from 'ducks/files/selectors'
import { loadAllFilesRoutine } from 'ducks/files/actions'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { Button, TextField, InputAdornment, CircularProgress } from '@mui/material'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import SearchIcon from '@mui/icons-material/Search'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import BlockIcon from '@mui/icons-material/Block'

const SUPPORTED_EXTENSIONS = [
  'pdf', 'dwg', 'doc', 'docx', 'jpg', 'jpeg', 'txt', 'png',
  'gif', 'svg', 'xlsx', 'xls', 'xlsm', 'msg', 'zip'
]

const getFileExtension = (fileName) => {
  const parts = fileName.split('.')
  return parts.length > 1 ? parts.pop().toLowerCase() : ''
}

const CheckFilesModal = ({ open, onClose }) => {
  const dispatch = useDispatch()
  const bp = useSelector(getSelectedBp)
  const files = useSelector(selectBpFilesList)
  const allFilesLoaded = useSelector(state => state.files.allFilesLoaded || false)
  const allFilesLoading = useSelector(state => state.files.isLoadingAllFiles || false)
  const inputRef = useRef(null)
  const [localFiles, setLocalFiles] = useState(null)
  const [filterQuery, setFilterQuery] = useState('')
  const [activeTab, setActiveTab] = useState('all')

  useEffect(() => {
    if (open && !allFilesLoaded && !allFilesLoading && bp?.id) {
      dispatch(loadAllFilesRoutine({ bpId: bp.id }))
    }
  }, [open, allFilesLoaded, allFilesLoading, bp?.id, dispatch])

  const results = useMemo(() => {
    if (!localFiles) return null

    const bpNamesSet = new Set()
    const bpStemsSet = new Set()
    files.forEach(f => {
      const name = (f.displayName || f.name || '').toLowerCase()
      if (name) {
        bpNamesSet.add(name)
        const stem = name.replace(/\.[^.]+$/, '')
        bpStemsSet.add(stem)
      }
    })

    const found = []
    const missing = []
    const unsupported = []

    localFiles.forEach(localName => {
      const ext = getFileExtension(localName)
      if (!ext || !SUPPORTED_EXTENSIONS.includes(ext)) {
        unsupported.push(localName)
      } else {
        const lower = localName.toLowerCase()
        const stem = lower.replace(/\.[^.]+$/, '')
        if (bpNamesSet.has(lower) || bpStemsSet.has(stem)) {
          found.push(localName)
        } else {
          missing.push(localName)
        }
      }
    })

    found.sort((a, b) => a.localeCompare(b))
    missing.sort((a, b) => a.localeCompare(b))
    unsupported.sort((a, b) => a.localeCompare(b))

    return { found, missing, unsupported }
  }, [localFiles, files])

  const filteredResults = useMemo(() => {
    if (!results) return null
    const q = filterQuery.toLowerCase()
    const filterFn = name => !q || name.toLowerCase().includes(q)
    return {
      found: results.found.filter(filterFn),
      missing: results.missing.filter(filterFn),
      unsupported: results.unsupported.filter(filterFn)
    }
  }, [results, filterQuery])

  const handleFolderSelect = e => {
    const fileList = e.target.files
    if (fileList && fileList.length > 0) {
      const names = Array.from(fileList).map(f => f.name)
      setLocalFiles(names)
      setFilterQuery('')
      setActiveTab('all')
    }
    e.target.value = ''
  }

  const handleClose = () => {
    setLocalFiles(null)
    setFilterQuery('')
    setActiveTab('all')
    onClose()
  }

  const supportedTotal = results ? results.found.length + results.missing.length : 0
  const displayFound = filteredResults ? filteredResults.found : []
  const displayMissing = filteredResults ? filteredResults.missing : []
  const displayUnsupported = filteredResults ? filteredResults.unsupported : []

  return (
    <Modal open={open} onClose={handleClose} title='Check Files'>
      <HiddenInput
        ref={inputRef}
        type='file'
        webkitdirectory=''
        multiple
        onChange={handleFolderSelect}
      />

      <SelectFolderRow>
        <Button
          variant='outlined'
          startIcon={<FolderOpenIcon />}
          onClick={() => inputRef.current?.click()}
          sx={{ textTransform: 'none' }}
        >
          Select Folder
        </Button>
        {results && (
          <SummaryText>
            <CheckCircleIcon sx={{ fontSize: 18, color: '#4caf50', mr: 0.5 }} />
            {results.found.length} found
            <CancelIcon sx={{ fontSize: 18, color: '#f44336', ml: 1.5, mr: 0.5 }} />
            {results.missing.length} missing
            {results.unsupported.length > 0 && (
              <>
                <BlockIcon sx={{ fontSize: 18, color: '#ff9800', ml: 1.5, mr: 0.5 }} />
                {results.unsupported.length} not supported
              </>
            )}
            <TotalText>of {localFiles?.length || 0} files</TotalText>
          </SummaryText>
        )}
      </SelectFolderRow>

      {results && (
        <>
          <TextField
            size='small'
            placeholder='Filter results...'
            value={filterQuery}
            onChange={e => setFilterQuery(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{ fontSize: 20, color: '#999' }} />
                </InputAdornment>
              )
            }}
          />

          <TabRow>
            <Tab active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
              All ({supportedTotal + results.unsupported.length})
            </Tab>
            <Tab active={activeTab === 'found'} onClick={() => setActiveTab('found')}>
              Found ({results.found.length})
            </Tab>
            <Tab active={activeTab === 'missing'} onClick={() => setActiveTab('missing')}>
              Missing ({results.missing.length})
            </Tab>
            <Tab active={activeTab === 'unsupported'} onClick={() => setActiveTab('unsupported')}>
              Not Supported ({results.unsupported.length})
            </Tab>
          </TabRow>

          <ResultsList>
            {(activeTab === 'all' || activeTab === 'unsupported') &&
              displayUnsupported.map(name => (
                <FileRow key={`unsupported-${name}`}>
                  <BlockIcon sx={{ fontSize: 16, color: '#ff9800', mr: 1, flexShrink: 0 }} />
                  <FileName>{name}</FileName>
                  <ExtBadge>.{getFileExtension(name) || '?'}</ExtBadge>
                </FileRow>
              ))}
            {(activeTab === 'all' || activeTab === 'missing') &&
              displayMissing.map(name => (
                <FileRow key={`missing-${name}`}>
                  <CancelIcon sx={{ fontSize: 16, color: '#f44336', mr: 1, flexShrink: 0 }} />
                  <FileName>{name}</FileName>
                </FileRow>
              ))}
            {(activeTab === 'all' || activeTab === 'found') &&
              displayFound.map(name => (
                <FileRow key={`found-${name}`}>
                  <CheckCircleIcon sx={{ fontSize: 16, color: '#4caf50', mr: 1, flexShrink: 0 }} />
                  <FileName>{name}</FileName>
                </FileRow>
              ))}
            {displayFound.length === 0 && displayMissing.length === 0 && displayUnsupported.length === 0 && (
              <EmptyState>No files match your filter.</EmptyState>
            )}
          </ResultsList>
        </>
      )}

      {!results && !allFilesLoading && (
        <EmptyState>
          Select a local folder to compare its files against the Building Passport.
          {allFilesLoaded && <FileCountHint>{files.length} files loaded from the Building Passport</FileCountHint>}
        </EmptyState>
      )}

      {allFilesLoading && (
        <LoadingState>
          <CircularProgress size={24} />
          <span>Loading all Building Passport files...</span>
        </LoadingState>
      )}
    </Modal>
  )
}

export default CheckFilesModal

const HiddenInput = styled.input`
  display: none;
`

const SelectFolderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const SummaryText = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
`

const TotalText = styled.span`
  margin-left: 8px;
  color: #999;
`

const TabRow = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
  border-bottom: 1px solid #eee;
`

const Tab = styled.button`
  padding: 8px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 13px;
  color: ${({ active }) => (active ? '#C24A2E' : '#666')};
  border-bottom: 2px solid ${({ active }) => (active ? '#C24A2E' : 'transparent')};
  font-weight: ${({ active }) => (active ? 600 : 400)};

  &:hover {
    color: #C24A2E;
  }
`

const ResultsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`

const FileRow = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 13px;

  &:hover {
    background: #f5f5f5;
  }
`

const FileName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ExtBadge = styled.span`
  margin-left: 8px;
  padding: 1px 6px;
  border-radius: 3px;
  background: #fff3e0;
  color: #e65100;
  font-size: 11px;
  font-weight: 500;
  flex-shrink: 0;
`

const EmptyState = styled.div`
  text-align: center;
  color: #999;
  padding: 32px 16px;
  font-size: 14px;
`

const FileCountHint = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #bbb;
`

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 16px;
  color: #999;
  font-size: 14px;
`
